import NextLink from 'next/link';
import { differenceInDays, parseJSON } from 'date-fns';

import useChallenges from 'lib/hooks/useChallenges';

import {
  Bubble,
  Card,
  Difficulty,
  flash,
  Heading,
  Icon,
  Languages,
  PopoverMenu,
} from '@frontendmentorio/design-system-v2';

import ConfirmPopoverMenuItem from 'features/shared/ConfirmPopoverMenuItem';
import Flag from 'features/shared/Flag';

export default function ChallengeCard({
  challenge,
  headingLevel = 'h2',
  isCompleted,
  inProgress,
}) {
  const { lock } = useChallenges();

  const href = `/challenges/${challenge.slug}`;
  const age = differenceInDays(new Date(), parseJSON(challenge.createdAt));

  const handleRemove = async () => {
    try {
      await lock(challenge);
      flash.success('Challenge successfully removed!');
    } catch (error) {
      flash.error(error.message);
    }
  };

  return (
    <Card tagName="li">
      <Card.Flags>
        {age <= 10 && <Flag type="new" />}

        <Flag type={challenge.type} />

        {inProgress && (
          <PopoverMenu
            buttonClassName="shadow-md"
            hasBackground
            icon="kebab"
            name="challenge options"
          >
            <ConfirmPopoverMenuItem
              className="text-rose-600"
              modal={{
                button: 'Remove challenge',
                description:
                  'This will remove this challenge from your challenges',
              }}
              onClick={handleRemove}
            >
              Remove challenge
            </ConfirmPopoverMenuItem>
          </PopoverMenu>
        )}
      </Card.Flags>

      <div className="relative">
        <Card.Image
          alt={`Visit the ${challenge.title} challenge page`}
          href={href}
          src={challenge.heroImage}
        />

        {isCompleted && (
          <div className="absolute-center-x bg-gradient-to-b bottom-0 flex from-[hsla(0,0%,0%,0)] gap-3 items-center justify-center pointer-events-none px-3 py-4 to-[hsla(0,0%,0%,0.45)] w-full">
            <Bubble className="bg-white" size="sm">
              <Icon className="text-emerald-400" name="check" style="solid" />
            </Bubble>

            <span className="font-secondary font-semibold  text-white [text-shadow:0_2px_2px_hsla(0,0%,0%,0.4)]">
              You’ve completed this challenge!
            </span>
          </div>
        )}
      </div>

      <Card.Content className="flex flex-col gap-3">
        <NextLink className="hover:underline" href={href}>
          <Heading level="5" tagName={headingLevel}>
            {challenge.title}
          </Heading>
        </NextLink>

        <div className="flex flex-wrap gap-3 items-center justify-between">
          <Languages languages={challenge.languages} />
          <Difficulty level={challenge.difficulty} />
        </div>

        <p className="text-neutral-500">{challenge.description}</p>
      </Card.Content>
    </Card>
  );
}
